import { apiClient } from './api';

export interface AttachmentDto {
  id: string;
  fileName: string;
  displayName: string;
  contentType: string;
  sizeBytes: number;
  isImage: boolean;
  imageWidth?: number;
  imageHeight?: number;
  ticketId?: string;
  conversationId?: string;
  uploadedById: string;
  uploadedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  isAvailable: boolean;
  createdAt: string;
  expiresAt?: string;
}

export interface AttachmentUploadDto {
  id: string;
  fileName: string;
  displayName: string;
  contentType: string;
  sizeBytes: number;
  isImage: boolean;
  imageWidth?: number;
  imageHeight?: number;
  downloadUrl: string;
  thumbnailUrl?: string;
  isSecure: boolean;
  createdAt: string;
}

export interface UploadConfig {
  allowedFileTypes: string[];
  maxFileSize: number;
  maxFileSizeFormatted: string;
  maxAttachments: number;
}

export interface ValidationResult {
  isValid: boolean;
  message: string;
}

export interface BulkOperationResponse {
  successCount: number;
  failureCount: number;
  errors: string[];
  processedIds: string[];
}

/**
 * Service for handling file attachments
 */
class AttachmentService {
  private baseUrl = '/attachments';

  /**
   * Upload a file attachment
   */
  async uploadFile(
    file: File,
    ticketId?: string,
    conversationId?: string,
    onProgress?: (progress: number) => void
  ): Promise<AttachmentUploadDto> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (ticketId) {
      formData.append('ticketId', ticketId);
    }
    
    if (conversationId) {
      formData.append('conversationId', conversationId);
    }

    try {
      const response = await apiClient.post<AttachmentUploadDto>(
        `${this.baseUrl}/upload`,
        formData,
        {
          params: { ticketId, conversationId },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(progress);
            }
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('File upload failed:', error);
      
      // Prefer whatever the API said - it is more specific than anything we can guess
      // (e.g. "File size exceeds 10MB limit" rather than a generic ceiling message).
      // Fall back to a status-specific message so a bodyless 4xx still tells the user
      // something actionable, then to the generic message.
      const statusFallbacks: Record<number, string> = {
        400: 'Invalid file format or size',
        403: 'You do not have permission to upload files',
        413: 'File size exceeds the maximum allowed limit',
      };
      let message = 'Failed to upload file. Please try again.';
      if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.status && statusFallbacks[error.response.status]) {
        message = statusFallbacks[error.response.status];
      } else if (error.message && error.message.includes('Failed to upload file')) {
        message = error.message;
      }
      const err = new Error(message);
      (err as any).response = error.response;
      throw err;
    }
  }

  /**
   * Upload multiple files
   */
  async uploadFiles(
    files: File[],
    ticketId?: string,
    conversationId?: string,
    onProgress?: (fileIndex: number, progress: number) => void
  ): Promise<AttachmentUploadDto[]> {
    if (!files || files.length === 0) {
      return [];
    }

    const uploads: AttachmentUploadDto[] = [];

    try {
      // Upload sequentially so a partial failure has a deterministic compensation path.
      // Successfully uploaded orphan records are removed before surfacing the error.
      for (let index = 0; index < files.length; index += 1) {
        uploads.push(await this.uploadFile(
          files[index],
          ticketId,
          conversationId,
          (progress) => onProgress?.(index, progress),
        ));
      }
      return uploads;
    } catch (error) {
      for (const upload of uploads.reverse()) {
        try {
          await this.deleteAttachment(upload.id);
        } catch (cleanupError) {
          console.error('Failed to remove a partial attachment upload:', cleanupError);
        }
      }
      console.error('Bulk file upload failed:', error);
      throw error;
    }
  }

  /**
   * Get attachment by ID
   */
  async getAttachment(id: string): Promise<AttachmentDto> {
    try {
      const response = await apiClient.get<AttachmentDto>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get attachment:', error);
      throw new Error('Failed to retrieve attachment');
    }
  }

  /**
   * Download attachment file
   */
  async downloadAttachment(id: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to download attachment:', error);
      throw new Error('Failed to download file');
    }
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      throw new Error('Failed to delete attachment');
    }
  }

  /**
   * Get attachments for a ticket
   */
  async getTicketAttachments(ticketId: string): Promise<AttachmentDto[]> {
    try {
      const response = await apiClient.get<AttachmentDto[]>(`${this.baseUrl}/ticket/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get ticket attachments:', error);
      throw new Error('Failed to retrieve ticket attachments');
    }
  }

  /**
   * Get attachments for a conversation
   */
  async getConversationAttachments(conversationId: string): Promise<AttachmentDto[]> {
    try {
      const response = await apiClient.get<AttachmentDto[]>(`${this.baseUrl}/conversation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get conversation attachments:', error);
      throw new Error('Failed to retrieve conversation attachments');
    }
  }

  /**
   * Get upload configuration for current user, specific role, or context
   */
  async getUploadConfig(role?: string, context?: string): Promise<UploadConfig> {
    try {
      const params: Record<string, string> = {};
      if (role) params.role = role;
      if (context) {
        params.context = context;
        if (context === 'public') {
          params.visibility = 'public';
        }
      }

      const response = await apiClient.get<UploadConfig>(`${this.baseUrl}/upload-config`, {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get upload config:', error);
      
      // Instead of hardcoded fallback, throw error to handle gracefully
      throw new Error('Unable to retrieve upload configuration. Please try again or contact support.');
    }
  }

  /**
   * Validate a file before upload
   */
  async validateFile(file: File): Promise<ValidationResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post<ValidationResult>(`${this.baseUrl}/validate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      console.error('File validation failed:', error);
      return {
        isValid: false,
        message: error.response?.data?.message || 'File validation failed'
      };
    }
  }

  /**
   * Format file size in human-readable format
   */
  formatFileSize(bytes: number): string {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }

  /**
   * Check if file type is an image
   */
  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Generate download URL for attachment
   */
  getDownloadUrl(attachmentId: string): string {
    return `${apiClient.defaults?.baseURL || ''}${this.baseUrl}/${attachmentId}/download`;
  }

  /**
   * Create a temporary preview URL for uploaded files
   */
  createPreviewUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.isImage(file.type)) {
        reject(new Error('File is not an image'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Failed to create preview'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }
}

export const attachmentService = new AttachmentService();

// Export convenience functions matching the instance methods for backward-compatible namespace imports
export const getUploadConfig = (role?: string, context?: string) => attachmentService.getUploadConfig(role, context);
export const uploadFile = (
  file: File,
  ticketId?: string,
  conversationId?: string,
  onProgress?: (progress: number) => void
) => attachmentService.uploadFile(file, ticketId, conversationId, onProgress);
export const uploadFiles = (
  files: File[],
  ticketId?: string,
  conversationId?: string,
  onProgress?: (fileIndex: number, progress: number) => void
) => attachmentService.uploadFiles(files, ticketId, conversationId, onProgress);
export const getAttachment = (id: string) => attachmentService.getAttachment(id);
export const getTicketAttachments = (ticketId: string) => attachmentService.getTicketAttachments(ticketId);
export const getConversationAttachments = (conversationId: string) => attachmentService.getConversationAttachments(conversationId);
export const validateFile = (file: File) => attachmentService.validateFile(file);
export const deleteAttachment = (id: string) => attachmentService.deleteAttachment(id);
export const downloadAttachment = (id: string, fileName?: string) => attachmentService.downloadAttachment(id, fileName);
export const createPreviewUrl = (file: File) => attachmentService.createPreviewUrl(file);
export const revokePreviewUrl = (url: string) => attachmentService.revokePreviewUrl(url);

export default attachmentService;
